import { Injectable } from '@angular/core';

// Models
import { MetaData, ResponseData } from 'src/app/platform/models/utils';
import { User, UserDebit, UserBalance, AddressData, PhoneData, AccountData, AccountMessage } from 'src/app/modules/account/models/user';

// Services
import { ApiService } from 'src/app/platform/services/api.service';
import { UserService } from 'src/app/modules/account/user.service';
import { PlatformService } from 'src/app/platform/services/platform.service';

/**
 * This class handles all actions related to an account
 */
@Injectable({
    providedIn: 'root'
})
export class AccountsService {
    user: User = new User();

    constructor(
        private apiService: ApiService,
        private userService: UserService,
        private platformService: PlatformService,
    ) {
      this.userService.currentUser.subscribe((user) => {
        this.user = user;
      });
    }

    /**
     * Check if there is any account established
     */
    isAccountSelected(): boolean{
      return this.user.isReady();
    }

    /**
     * Gets user accounts
     */
    accountsGet(): Array<AccountData>{
        let accounts: Array<AccountData> = [];

        if (this.user !== null) {
            accounts = this.user.accounts;
        }

        return accounts;
    }

    /**
     * Get account data
     */
    accountGet(account: number): Promise<ResponseData>{
      return new Promise((resolve, reject) => {
        const headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Bearer ' + this.user.token
        };

        this.apiService.request('get', 'clientes/' + account, {}, headers).toPromise().then((success: any) => {
          resolve(this.apiService.successDataPrepare(success.response.message, success));
        }, (error) => {
          reject(this.apiService.errorDataPrepare(error.response.message, error));
        });
      });
    }

    /**
     * Load account data from backend
     */
    accountsLoad(): Promise<ResponseData>{
      return new Promise((resolve, reject) => {
        const headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Bearer ' + this.user.token
        };

        this.apiService.request('get', 'usuarios/' + this.user.id, {}, headers).toPromise().then((success: any) => {
          if (success.response.status) {
              resolve(this.apiService.successDataPrepare(success.response.message, success));
          } else {
              reject(this.apiService.errorDataPrepare(success.response.message, success));
          }
        }, (error) => {
            reject(this.apiService.errorDataPrepare(error.response.message, error));
        });
      });
    }

    /**
     * Get current account
     */
    accountCurrentGet(): any{
      return this.user.client;
    }

    /**
     * Set account to manage
     */
    accountSet(account: number): Promise<any>{
      return new Promise((resolve, reject) => {
        const headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': 'Bearer ' + this.user.token
        };

        this.apiService.request('get', 'clientes/' + account, {}, headers).toPromise().then((result: any) => {
          this.userService.update(this.accountDataStructure(result));

          resolve(true);
        }, (error) => {
          reject(this.apiService.errorDataPrepare(error.response.message, error));
        });
      });
    }

    /**
     * Updates data regarding account information
     */
    accountUpdate(firstname: string, lastname: string, dni: string, emails: Array<string>, phones: Array<any>, addressConection: AddressData, addressBilling: AddressData): Promise<ResponseData> {
        return new Promise((resolve, reject) => {
            const headers = {
              'Content-Type': 'application/json',
              'Authorization': 'Bearer ' + this.user.token
            };

            const data = {
                apellido: lastname,
                nombre: firstname,
                dni: dni,
                eMails: emails,
                telefonos: phones,
                domicilioConexion: {
                  provinciaId: addressConection.city.state.id,
                  departamentoId: addressConection.city.department.id,
                  localidadId: addressConection.city.id,
                  calleId: addressConection.street.id,
                  altura: addressConection.number,
                  apartamento: addressConection.apartment,
                },
                domicilioFacturacion: {
                  provinciaId: addressBilling.city.state.id,
                  departamentoId: addressBilling.city.department.id,
                  localidadId: addressBilling.city.id,
                  calleId: addressBilling.street.id,
                  altura: addressBilling.number,
                  apartamento: addressBilling.apartment
                }
            };

            this.apiService.request('post', 'clientes/' + this.user.client, data, headers).toPromise().then((success: any) => {
              if (success.response.status) {
                resolve(this.apiService.successDataPrepare('', success));
              } else {
                reject(this.apiService.errorDataPrepare(success.response.message, success));
              }
            }, (error) => {
              console.log(error);
              reject(this.apiService.errorDataPrepare(error.response.message, error));
            });
        });
    }

    /**
     * Request the total cancellation of an account
     */
    accountDelete(reason: number, comments: string, phone: string, email: string, schedule: string, contactByPhone: boolean, contactByEmail: boolean): Promise<any> {
        return new Promise((resolve, reject) => {
            const headers = {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Authorization': 'Bearer ' + this.user.token
            };

            const data = {
                motivoBajaId: reason,
                observaciones: comments,
                telefonoContacto: phone,
                mailContacto: email,
                horarioContacto: schedule,
                contactarTelefono: contactByPhone,
                contactarMail: contactByEmail
            };
            
            this.apiService.request('delete', 'contratos/clienteId=' + this.user.client, data, headers).toPromise().then((success: any) => {
              if (success.response.status) {
                resolve(this.apiService.successDataPrepare(success.response.message, success));
              } else {
                reject(this.apiService.errorDataPrepare(success.response.message, success));
              }
            }, (error) => {
              reject(this.apiService.errorDataPrepare(error.response.message, error));
            });
        });
    }

    /**
     * Cancel the total cancellation of an account
     */
    accountDeleteCancel(): Promise<any> {
        return new Promise((resolve, reject) => {
            const headers = {
              'Content-Type': 'application/x-www-form-urlencoded',
              'Authorization': 'Bearer ' + this.user.token
            };
      
            this.apiService.request('post', 'contratos/anularbajas/clienteId=' + this.user.client, {}, headers).toPromise().then((success: any) => {
              if (success.response.status) {
                resolve(this.apiService.successDataPrepare(success.response.message, success));
              } else {
                reject(this.apiService.errorDataPrepare(success.response.message, success));
              }
            }, (error) => {
              reject(this.apiService.errorDataPrepare(error.response.message, error));
            });
        });
    }

    /**
     * Generate the structure to create the account object
     */
    accountDataStructure(data: any): User {
      const userData = new User();

      userData.configData({
          client: data.id,
          firstname: this.platformService.capitalize(data.nombre),
          lastname: this.platformService.capitalize(data.apellido),
          dni: data.dni,
          phone: data.telefono,
          phones: new PhoneData().processData(data.telefonos),
          email: data.eMail,
          emails: data.eMails,
          addressBilling: new AddressData().configData(data.domicilioFacturacion),
          addressConection: new AddressData().configData(data.domicilioConexion),
          addressConectionUpdated: data.domicilioConexionModificable,
          status: data.estado,

          debit: new UserDebit().configData(data),
          balance: new UserBalance().configData(data),

          subsidiary: new MetaData().configData(data.sucursal),
          segment: new MetaData().configData(data.segmento),
          zone: new MetaData().configData(data.zona),
          saldo: data.saldo,
          debtExpired: data.deudaVencida,
          invoicesUnpaid: data.facturasImpagas,
          invoicesUnpaidExpired: data.facturasImpagasVencidas,
          flowdatStatus: data.estadoFlowdat,
          services: data.servicios,
          unsuscribeAccount: data.bajaClientePendiente,
          unsuscribeServices: data.bajaContratoPendiente,
          paymentsAllow: data.permitePago,
          messages: Array.isArray(data.mensajes) ? data.mensajes.map((message: any) => new AccountMessage(message)) : [],

          id: this.userService.currentUser.value.id,
          token: this.userService.currentUser.value.token,
          username: this.userService.currentUser.value.username,
          accounts: this.userService.currentUser.value.accounts,
          rol: this.userService.currentUser.value.rol,
          responseData: data,
      });

      return userData;
    }

    /**
     * Call the api to generate a new password for the user and send it by email
     */
    activate(hash: string): Promise<ResponseData> {
      return new Promise((resolve, reject) => {
          const headers = {
              'Content-Type': 'application/x-www-form-urlencoded'
          };

          this.apiService.request('post', 'usuarios/passwordgenerate?token=' + hash, {}, headers).toPromise().then((success: any) => {
              if (success.status) {
                  resolve(this.apiService.successDataPrepare(success.message, success));
              } else {
                  reject(this.apiService.errorDataPrepare(success.message, success));
              }
          }, (error) => {
              reject(this.apiService.errorDataPrepare(error.message, error));
          });
      });
    }

    /**
     * Updates information from the list of accounts stored
     */
    accountsDataUpdate(): void {
      this.accountsLoad().then((success) => {
        const accountsSetup: Array<AccountData> = [];

        success.result.cuentas.forEach((account: any): void => {
          accountsSetup.push(new AccountData(account));
        });

        this.userService.updateData('accounts', accountsSetup);
      }).catch((error) => {
        console.log('Account data not updated');
      });
    }
}
